import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, X, BadgeCheck } from "lucide-react";

export default function AdminWithdrawals() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [action, setAction] = useState(null); // {type:'approve'|'paid'|'reject', row}
  const [utr, setUtr] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/withdrawals${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      if (action.type === "approve") await api.post(`/admin/withdrawals/${action.row.txn_id}/approve`);
      else if (action.type === "paid") await api.post(`/admin/withdrawals/${action.row.txn_id}/paid`, { utr });
      else await api.post(`/admin/withdrawals/${action.row.txn_id}/reject`, { reason });
      toast.success(action.type === "reject" ? "Rejected & funds returned" : action.type === "paid" ? "Marked as Paid" : "Approved");
      setAction(null); setUtr(""); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Withdrawal Requests" desc="Reserved funds are locked until you mark Paid (with UTR) or Reject (auto-refund)." />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="bg-slate-900/60">
          {["pending", "approved", "paid", "rejected", "all"].map((s) => <TabsTrigger key={s} value={s} data-testid={`wd-filter-${s}`} className="capitalize">{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-400">Txn ID</TableHead><TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">Payout Details</TableHead>
                <TableHead className="text-slate-400">UTR</TableHead><TableHead className="text-slate-400">Date (IST)</TableHead>
                <TableHead className="text-slate-400">Status</TableHead><TableHead className="text-slate-400 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!rows ? <TableRow><TableCell colSpan={8} className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400 mx-auto" /></TableCell></TableRow> :
                rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">No withdrawal requests.</TableCell></TableRow> :
                rows.map((t) => (
                  <TableRow key={t.id} className="border-white/5" data-testid={`withdrawal-row-${t.txn_id}`}>
                    <TableCell className="font-mono text-xs text-slate-400">{t.txn_id}</TableCell>
                    <TableCell><div className="font-medium text-sm">{t.user.name}</div><div className="text-xs text-slate-500">{t.user.email}</div></TableCell>
                    <TableCell className="font-mono text-rose-300">{formatINR(t.amount)}</TableCell>
                    <TableCell className="text-xs text-slate-400 max-w-[180px] truncate" title={t.payout_details}>{t.payout_details || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{t.utr || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell className="text-right">
                      {(t.status === "pending" || t.status === "approved") ? (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" onClick={() => { setAction({ type: "paid", row: t }); setUtr(""); }} data-testid={`paid-wd-${t.txn_id}`} title="Mark Paid" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] h-8"><BadgeCheck className="h-4 w-4" /></Button>
                          {t.status === "pending" && <Button size="sm" variant="outline" onClick={() => setAction({ type: "approve", row: t })} data-testid={`approve-wd-${t.txn_id}`} title="Approve" className="border-cyan-500/30 text-cyan-300 bg-transparent hover:bg-cyan-500/10 h-8"><Check className="h-4 w-4" /></Button>}
                          <Button size="sm" variant="outline" onClick={() => setAction({ type: "reject", row: t })} data-testid={`reject-wd-${t.txn_id}`} title="Reject" className="border-rose-500/30 text-rose-300 bg-transparent hover:bg-rose-500/10 h-8"><X className="h-4 w-4" /></Button>
                        </div>
                      ) : <span className="text-xs text-slate-500">{t.remark || "—"}</span>}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-[#121a28] border-white/10">
          <DialogHeader><DialogTitle className="font-display">{action?.type === "paid" ? "Mark as Paid" : action?.type === "approve" ? "Approve Withdrawal" : "Reject Withdrawal"}</DialogTitle></DialogHeader>
          {action && (
            <div className="space-y-3">
              <div className="text-sm text-slate-400">{action.row.user.name} · {formatINR(action.row.amount)}</div>
              <div className="rounded-lg bg-black/20 border border-white/10 p-3 text-xs font-mono text-slate-300">{action.row.payout_details}</div>
              {action.type === "paid" && <div><Label className="text-slate-300">Payout UTR / Reference</Label><Input value={utr} onChange={(e) => setUtr(e.target.value)} data-testid="paid-utr-input" placeholder="Enter UTR after payout" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>}
              {action.type === "reject" && <div><Label className="text-slate-300">Rejection Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-wd-reason" placeholder="Reason (funds will be refunded)" className="mt-1.5 bg-slate-900/60 border-slate-700" /></div>}
              {action.type === "approve" && <p className="text-sm text-slate-400">Approve keeps funds reserved. Mark Paid later with the UTR to deduct.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="border-slate-700 bg-transparent">Cancel</Button>
            <Button onClick={submit} disabled={busy || (action?.type === "paid" && !utr) || (action?.type === "reject" && !reason)} data-testid="confirm-wd-action"
              className={action?.type === "reject" ? "bg-rose-500 hover:bg-rose-400 text-white font-semibold" : "bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold"}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : action?.type === "paid" ? "Confirm Paid" : action?.type === "approve" ? "Approve" : "Reject & Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
